import type { EmailAdvisor, EmailStatus } from '../../api/types';

const IN_QUEUE: EmailStatus[] = ['queued', 'sending'];

export interface SendPlan {
  /** Will receive their report. */
  recipients: EmailAdvisor[];
  /** Have no address: they will not receive it. */
  withoutEmail: EmailAdvisor[];
  /** Already waiting in the queue: not queued twice. */
  alreadyInQueue: EmailAdvisor[];
  /** Among the recipients, those who already received this quarter's report. */
  resend: EmailAdvisor[];
}

/** Who a send would reach, the same rules the API applies. `ids` omitted: everyone. */
export function planSend(advisors: EmailAdvisor[], ids?: number[]): SendPlan {
  const wanted = ids ? advisors.filter((a) => ids.includes(a.id)) : advisors;
  const withoutEmail = wanted.filter((a) => !a.email);
  const alreadyInQueue = wanted.filter((a) => a.email && a.lastEmail && IN_QUEUE.includes(a.lastEmail.status));
  const recipients = wanted.filter((a) => a.email && !alreadyInQueue.includes(a));
  return { recipients, withoutEmail, alreadyInQueue, resend: recipients.filter((a) => a.lastEmail?.status === 'sent') };
}

export interface SendProgress {
  queued: number;
  sent: number;
  failed: number;
  cancelled: number;
  /** Emails of the quarter so far (latest per advisor). */
  total: number;
  active: boolean;
}

export function sendProgress(advisors: EmailAdvisor[]): SendProgress {
  const progress: SendProgress = { queued: 0, sent: 0, failed: 0, cancelled: 0, total: 0, active: false };
  for (const { lastEmail } of advisors) {
    if (!lastEmail) continue;
    progress.total += 1;
    if (IN_QUEUE.includes(lastEmail.status)) progress.queued += 1;
    else progress[lastEmail.status as 'sent' | 'failed' | 'cancelled'] += 1;
  }
  progress.active = progress.queued > 0;
  return progress;
}

/** "1 asesor" / "2 asesores". */
export const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** "unos 2 minutos" for N emails sent one every `intervalSeconds`. */
export function estimateDuration(count: number, intervalSeconds: number): string {
  const seconds = Math.max(0, count - 1) * intervalSeconds + count * 2; // + ~2 s to render each PDF
  if (seconds < 60) return 'menos de un minuto';
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? 'un minuto' : `unos ${minutes} minutos`;
}

/** Why an email failed, in plain Spanish, and what to do about it. */
export function describeEmailError(code: string | null): string {
  switch (code) {
    case 'UnauthenticatedFromAddress':
      return 'IDHub no reconoce el remitente: hay que registrarlo para esta app en «Remitentes de correo».';
    case 'MAIL_KEY_REJECTED':
      return 'IDHub ha rechazado la clave de envío: hay que regenerarla y actualizar IDHUB_MAIL_API_KEY.';
    case 'TOO_LARGE':
      return 'IDHub ha rechazado el correo por tamaño: hay que ampliar su límite de envío.';
    case 'INVALID_REQUEST':
    case 'NoRecipients':
      return 'La dirección de correo no es válida: revísala en Asesores y vuelve a intentarlo.';
    case 'NO_DATA':
      return 'El asesor ya no tiene datos en este trimestre.';
    case 'IdHubProxyUnreachable':
    case 'IdHubProxyInvalidResponse':
    case 'IdHubProxyNotConfigured':
    case 'NETWORK':
    case 'IDHUB_ERROR':
    case 'RATE_LIMITED':
      return 'El servidor de correo no respondió tras varios intentos. Vuelve a intentarlo más tarde.';
    case 'REPORT_FAILED':
      return 'No se pudo generar el PDF tras varios intentos. Vuelve a intentarlo más tarde.';
    default:
      return code ? `El servidor de correo lo rechazó (${code}).` : 'No se pudo enviar.';
  }
}

export const STATUS_LABEL: Record<EmailStatus, string> = {
  queued: 'En cola',
  sending: 'Enviando',
  sent: 'Enviado',
  failed: 'No enviado',
  cancelled: 'Detenido',
};
